/**
 * 드롭다운 선택 컴포넌트
 *
 * 디자인 시스템의 input-base 스타일을 활용한 선택 드롭다운입니다.
 * label, 에러 상태, 도움말 텍스트, placeholder를 지원합니다.
 *
 * @component
 * @example
 * // 기본 선택 드롭다운
 * const [genre, setGenre] = useState('');
 * <AppSelect
 *   label="장르"
 *   options={[
 *     { value: 'pop', label: '팝' },
 *     { value: 'rock', label: '록' },
 *     { value: 'jazz', label: '재즈' }
 *   ]}
 *   placeholder="장르를 선택하세요"
 *   value={genre}
 *   onChange={(e) => setGenre(e.target.value)}
 * />
 *
 * @example
 * // 에러 상태
 * <AppSelect
 *   label="음악 스타일"
 *   options={options}
 *   error={!!styleError}
 *   helperText={styleError || "선택해주세요"}
 * />
 *
 * @typedef {object} AppSelectOption
 * @property {string | number} value - 옵션 값
 * @property {string} label - 옵션 표시 텍스트
 * @property {boolean} [disabled] - 옵션 비활성화 여부
 *
 * @typedef {object} AppSelectProps
 * @property {string} [label] - 선택 필드 위에 표시할 라벨
 * @property {AppSelectOption[]} options - 선택 가능한 옵션 배열
 * @property {string} [placeholder] - 선택하지 않았을 때 표시 텍스트
 * @property {boolean} [error=false] - 에러 상태
 * @property {string} [helperText] - 도움말 또는 에러 메시지
 * @property {boolean} [disabled=false] - 비활성화 여부
 * @property {string} [className] - 추가 CSS 클래스
 *
 * @returns {JSX.Element} 스타일이 적용된 드롭다운
 */

import { SelectHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface AppSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface AppSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  error?: boolean;
  helperText?: string;
  label?: string;
  options: readonly AppSelectOption[];
  placeholder?: string;
}

export const AppSelect = forwardRef<HTMLSelectElement, AppSelectProps>(
  (
    {
      error,
      helperText,
      label,
      options,
      placeholder,
      className,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const helperId = helperText && selectId ? `${selectId}-helper` : undefined;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-neutral-700 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            aria-invalid={error || undefined}
            aria-describedby={helperId}
            className={cn(
              'input-base appearance-none pr-10',
              error && 'input-error',
              disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-600 pointer-events-none" aria-hidden="true">
            ▼
          </span>
        </div>
        {helperText && (
          <p
            id={helperId}
            className={cn(
              'text-sm mt-1',
              error ? 'text-error-600' : 'text-neutral-500'
            )}
            role={error ? 'alert' : undefined}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

AppSelect.displayName = 'AppSelect';
